import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TFunction } from '@lyearn/i18n';
import { ArrowIosDownward, CheckMark } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import AutocompleteSearch from '@/components/AutocompleteSearch';
import { AutocompleteSearchProps } from '@/components/AutocompleteSearch/types';
import Loader from '@/components/Form/components/Conditions/components/Loader';
import { GroupStatusEnum, GroupTypeEnum } from '@/types/schema';

import useGroup from '../../hooks/useGroup';
import { ContentCollectionFragment } from '../../views/GroupManager/graphql/fragments/generated/ContentCollection';
import { UserCollectionFragment } from '../../views/GroupManager/graphql/fragments/generated/UserCollection';
import useGroups from '../../views/GroupManager/hooks/useGroups';
import { getGroupImage } from '../../views/GroupManager/utils';
import { IMAGE_CLASS_OVERRIDES } from './consts';

const renderGroup =
  (t: TFunction, type: GroupTypeEnum, selectedGroupId?: string | null) =>
  (group: UserCollectionFragment | ContentCollectionFragment) => {
    const isSelected = selectedGroupId === group.id;

    return (
      <Stack alignItems="center" className="mb-2 w-full p-12">
        {getGroupImage({ group, type, overrides: IMAGE_CLASS_OVERRIDES, t })}
        <Stack alignItems="center" className="w-full overflow-hidden ms-8" justifyContent="between">
          <Typography
            className="truncate"
            color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
            variant="body-short-01">
            {group.name}
          </Typography>
          {isSelected ? (
            <CheckMark className="h-24 w-24 flex-shrink-0 ms-12" fillColorToken="icon-active" />
          ) : null}
        </Stack>
      </Stack>
    );
  };

interface GroupSearchProps {
  onChange: (value?: string) => void;
  selectedValue?: string | null;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  type: GroupTypeEnum;
  size?: AutocompleteSearchProps<any>['size'];
  placeholder?: string;
  crossSubWorkspace?: boolean;
}

function GroupAutocomplete(props: GroupSearchProps) {
  const { t } = useTranslation();
  const [searchStr, setSearchStr] = useState('');
  const { selectedValue, onChange, disabled, error, type, size, placeholder, crossSubWorkspace } =
    props;
  const { groups, isLoading, fetchMore } = useGroups({
    filters: {
      name: searchStr,
      systemGenerated: false,
      status: [GroupStatusEnum.Published],
      types: [type],
      crossSubWorkspace,
    },
  });

  const { group: selectedGroup, fetching: fetchingSelected } = useGroup({
    groupId: selectedValue,
    type,
  });
  const onSelect = useCallback(
    (groupId?: string) => {
      onChange(groupId);
    },
    [onChange],
  );
  const hasError = useMemo(
    () => Boolean(error && !disabled && !selectedValue),
    [disabled, error, selectedValue],
  );

  const startAdornment = useCallback(
    () => getGroupImage({ group: selectedGroup!, type, overrides: IMAGE_CLASS_OVERRIDES, t }),
    [selectedGroup, type, t],
  );

  if (isLoading || fetchingSelected) {
    return <Loader className={size === 'large' ? 'h-48' : 'h-40'} />;
  }

  return (
    <Stack
      className={cn({
        'cursor-not-allowed': disabled,
      })}
      direction="col">
      <AutocompleteSearch
        disableClearable
        className="flex-1"
        clearOnBlur={false}
        closeIcon={null}
        data={groups}
        data-id="group-and-collection-search"
        error={hasError}
        fetchMore={fetchMore}
        initialValue={selectedGroup?.name}
        placeholder={placeholder ?? t('Common:Actions.Select')}
        popupIcon={<ArrowIosDownward fillColorToken="icon-placeholder" />}
        renderers={{
          renderOption: renderGroup(t, type, selectedValue),
          renderStartAdornment: startAdornment,
          renderEndAdornment: () => null,
        }}
        size={size}
        onChange={onSelect}
        onInputChange={setSearchStr}
      />
    </Stack>
  );
}

export default GroupAutocomplete;
